import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { postTermText, type PostTermRule } from "@talentkontor/domain";
import { api } from "../../data";
import { dateOf, pctOf, useLang, useT } from "./sprache";
import { useCreatorAction, useCreatorContext } from "./ui";
import { loadKitSelf, loadPostTermInfo, proposeKit, themenAusText, type KitSelf } from "./kit-daten";

/**
 * Media-Kit im Creator-Portal (0074): Was die Agentur über dich an Marken teilt (Profil, Kennzahlen, Referenzen, aktive Links mit
 * Aufrufen) und dein Vorschlag für Kurzbeschreibung und Themen, den die Agentur freigibt. Preise nennt die Seite nicht, nur ob
 * ein Link sie zeigt.
 */

const PLATFORM: Record<string, string> = {
  instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", twitch: "Twitch", linkedin: "LinkedIn", pinterest: "Pinterest", snapchat: "Snapchat",
  facebook: "Facebook", x: "X", podcast: "Podcast", blog: "Blog", other: "Sonstige",
};
const IG_BLOCK = (b: string, t: (de: string, en: string) => string): string => ({
  profile: t("Follower und Beiträge", "Followers and posts"), reach: t("Reichweite", "Reach"), views: t("Aufrufe", "Views"),
  interactions: t("Interaktionen", "Interactions"), age: t("Alter der Zielgruppe", "Audience age"), gender: t("Geschlecht der Zielgruppe", "Audience gender"),
  countries: t("Länder der Zielgruppe", "Audience countries"),
}[b] ?? b);

export function MediaKitBereich() {
  const { scope } = useCreatorContext();
  const t = useT();
  const q = useQuery({ queryKey: ["creator", scope.talentId, "mediakit"], queryFn: () => loadKitSelf(api, scope.talentId) });
  if (q.isPending || q.error || !q.data) return null;
  return <Inhalt key={q.data.proposal?.at ?? q.data.decision?.at ?? "leer"} talentId={scope.talentId} k={q.data} t={t} />;
}

function Inhalt({ talentId, k, t }: { talentId: string; k: KitSelf; t: (de: string, en: string) => string }) {
  const lang = useLang();
  const { ws } = useCreatorContext();
  const { run, busy } = useCreatorAction();
  const [offen, setOffen] = useState(false);
  const [bioDe, setBioDe] = useState(k.proposal?.bio_de ?? k.bio_de ?? "");
  const [bioEn, setBioEn] = useState(k.proposal?.bio_en ?? k.bio_en ?? "");
  const [themen, setThemen] = useState((k.proposal?.topics ?? k.topics).join(", "));
  const [fehler, setFehler] = useState<string | null>(null);
  const tz = ws.org.timezone;

  async function senden() {
    const topics = themenAusText(themen);
    if (!bioDe.trim() && !bioEn.trim() && !topics.length) return setFehler(t("Bitte eine Kurzbeschreibung oder Themen angeben.", "Please add a short bio or topics."));
    if (topics.length > 12 || topics.some((x) => x.length < 2 || x.length > 40)) return setFehler(t("Höchstens 12 Themen mit je 2 bis 40 Zeichen.", "At most 12 topics with 2 to 40 characters each."));
    setFehler(null);
    const ok = await run("kit", () => proposeKit(api, talentId, { bioDe, bioEn, topics }), t("Vorschlag an deine Agentur geschickt.", "Suggestion sent to your agency."));
    if (ok !== undefined) setOffen(false);
  }

  const geteilt = [
    t("Name und Profilbild", "Name and profile picture") + (k.has_image ? "" : t(" (noch kein Bild)", " (no picture yet)")),
    k.bio_de || k.bio_en ? t("Kurzbeschreibung", "Short bio") : null,
    k.topics.length ? t(`Themen: ${k.topics.join(", ")}`, `Topics: ${k.topics.join(", ")}`) : null,
    ...k.channels.map((c) => `${c.platform === "other" ? t("Sonstige", "Other") : PLATFORM[c.platform] ?? c.platform}${c.handle ? ` ${c.handle}` : ""}${c.followers != null ? ` · ${new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE").format(c.followers)} ${t("Follower", "followers")}` : ""}${c.as_of ? ` · ${t("Stand", "as of")} ${dateOf(c.as_of, lang, tz)}` : ""}`),
    k.instagram_shared && k.instagram_blocks.length
      ? t(`Instagram (verbunden, nur mit deiner Einwilligung): ${k.instagram_blocks.map((b) => IG_BLOCK(b, t)).join(", ")}`, `Instagram (connected, only with your consent): ${k.instagram_blocks.map((b) => IG_BLOCK(b, t)).join(", ")}`)
      : null,
    k.references.length ? t(`Referenzen: ${k.references.map((r) => [r.brand, r.year].filter(Boolean).join(" ")).join(", ")}`, `References: ${k.references.map((r) => [r.brand, r.year].filter(Boolean).join(" ")).join(", ")}`) : null,
    k.rates_count ? t("Preise: nur in Links, für die deine Agentur sie einschaltet", "Rates: only in links where your agency turns them on") : null,
  ].filter((x): x is string => !!x);

  return (
    <section className="panel stack" aria-labelledby="mediakit-titel">
      <h2 id="mediakit-titel">{t("Dein Media-Kit", "Your media kit")}</h2>
      <p>{t("Deine Agentur kann ein Media-Kit von dir als persönlichen, befristeten Link an Marken schicken, allein oder in einer Auswahl mehrerer Creator. Das wird dabei geteilt:",
        "Your agency can send a media kit about you to brands as a personal, time-limited link, on its own or in a selection of several creators. This is what gets shared:")}</p>
      <ul>{geteilt.map((g) => <li key={g}>{g}</li>)}</ul>
      {(k.bio_de || k.bio_en) && (
        <blockquote className="stack tk-gap-sm tk-umbruch">
          {k.bio_de && <p lang="de">{k.bio_de}</p>}
          {k.bio_en && <p lang="en">{k.bio_en}</p>}
        </blockquote>
      )}

      <h3>{t("Aktive Links", "Active links")}</h3>
      {k.links.length === 0 ? <p>{t("Zurzeit ist kein Link mit dir aktiv.", "There is no active link with you right now.")}</p> : (
        <div className="tablewrap tk-cardtable tk-cardnarrow">
          <table>
            <caption className="sr-only">{t("Aktive Links mit deinem Media-Kit", "Active links with your media kit")}</caption>
            <thead><tr><th scope="col">{t("Für", "For")}</th><th scope="col">{t("Gültig bis", "Valid until")}</th><th scope="col">{t("Umfang", "Scope")}</th><th scope="col">{t("Aufrufe", "Views")}</th></tr></thead>
            <tbody>
              {k.links.map((l, i) => (
                <tr key={i}>
                  <td data-label={t("Für", "For")}>{l.brand ?? l.title ?? t("Marke", "Brand")}{l.kind === "pitch" ? t(` · Auswahl mit ${l.others} weiteren`, ` · selection with ${l.others} others`) : ""}</td>
                  <td data-label={t("Gültig bis", "Valid until")}>{dateOf(l.expires_at, lang, tz)}</td>
                  <td data-label={t("Umfang", "Scope")}>{[t("Profil", "Profile"), l.show_instagram && k.instagram_shared ? "Instagram" : null, l.show_references ? t("Referenzen", "References") : null, l.show_rates ? t("Preise", "Rates") : null].filter(Boolean).join(", ")}</td>
                  <td data-label={t("Aufrufe", "Views")}>{l.views}{l.last_view ? ` · ${t("zuletzt", "last")} ${dateOf(l.last_view, lang, tz)}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>{t("Kurzbeschreibung und Themen vorschlagen", "Suggest short bio and topics")}</h3>
      {k.proposal && <div className="notice" role="status">{t(`Dein Vorschlag vom ${dateOf(k.proposal.at, lang, tz)} liegt bei deiner Agentur. Bis zur Freigabe bleibt der bisherige Text.`, `Your suggestion from ${dateOf(k.proposal.at, lang, tz)} is with your agency. The current text stays until it is approved.`)}</div>}
      {!k.proposal && k.decision && (
        <p className="source-note">
          {k.decision.decision === "accepted"
            ? t(`Dein letzter Vorschlag wurde am ${dateOf(k.decision.at, lang, tz)} übernommen.`, `Your last suggestion was accepted on ${dateOf(k.decision.at, lang, tz)}.`)
            : t(`Dein letzter Vorschlag wurde am ${dateOf(k.decision.at, lang, tz)} nicht übernommen.`, `Your last suggestion was not accepted on ${dateOf(k.decision.at, lang, tz)}.`)}
          {k.decision.note ? ` ${t("Hinweis der Agentur", "Note from your agency")}: ${k.decision.note}` : ""}
        </p>
      )}
      {!offen ? (
        <div className="row"><button type="button" onClick={() => setOffen(true)}>{k.proposal ? t("Vorschlag ändern", "Change suggestion") : t("Vorschlag machen", "Make a suggestion")}</button></div>
      ) : (
        <div className="stack">
          <label>{t("Kurzbeschreibung (Deutsch)", "Short bio (German)")}<textarea rows={4} maxLength={1200} value={bioDe} onChange={(e) => setBioDe(e.target.value)} /></label>
          <label>{t("Kurzbeschreibung (Englisch)", "Short bio (English)")}<textarea rows={4} maxLength={1200} value={bioEn} onChange={(e) => setBioEn(e.target.value)} /></label>
          <label>{t("Themen, mit Komma getrennt", "Topics, separated by commas")}<input type="text" value={themen} onChange={(e) => setThemen(e.target.value)} placeholder={t("z. B. Outdoor, Reisen, Fotografie", "e.g. outdoor, travel, photography")} /></label>
          {fehler && <p className="tk-error" role="alert">{fehler}</p>}
          <div className="row">
            <button type="button" className="primary" disabled={busy === "kit"} aria-busy={busy === "kit"} onClick={() => void senden()}>{busy === "kit" ? t("Wird gesendet …", "Sending …") : t("An die Agentur senden", "Send to your agency")}</button>
            <button type="button" onClick={() => setOffen(false)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </div>
      )}
    </section>
  );
}

/** Nachprovision nach dem Austritt (0074): auch nach dem Archivieren sichtbar (wie Widerruf und Trennen). */
export function NachprovisionHinweis({ talentId }: { talentId: string }) {
  const t = useT();
  const lang = useLang();
  const q = useQuery({ queryKey: ["creator", talentId, "post-term"], queryFn: () => loadPostTermInfo(api) });
  const info = (q.data ?? []).find((x) => x.talent_id === talentId);
  if (!info) return null;
  const pct = (bp: number) => pctOf(bp / 100, lang);
  const rule: PostTermRule = { months: info.months, ...(info.steps ? { steps: info.steps } : { bp: info.bp ?? 0 }), scope: info.scope, ...(info.after_bp ? { after_bp: info.after_bp } : {}) };
  const monatlich = info.deals.some((d) => d.monthly);
  return (
    <section className="notice stack" aria-labelledby="nachprovision-titel">
      <h2 id="nachprovision-titel">{t("Nachprovision nach deinem Austritt", "Post-term commission after you left")}</h2>
      <p>
        {t(`Mit ${info.org_name} gilt vom ${dateOf(info.from, lang)} bis ${dateOf(info.until, lang)} eine Nachprovision: ${postTermText(rule, pct, "de")}.`,
          `With ${info.org_name}, a post-term commission applies from ${dateOf(info.from, lang)} to ${dateOf(info.until, lang)}: ${postTermText(rule, pct, "en")}.`)}{" "}
        {info.scope === "initiated"
          ? t("Sie gilt für Kooperationen aus deiner Zeit bei der Agentur und für solche, die sie angebahnt hat und die in diesem Zeitraum zustande kommen.",
            "It applies to collaborations from your time with the agency and to those it initiated that are concluded in this period.")
          : t("Sie gilt für Kooperationen, die während deiner Zeit bei der Agentur abgeschlossen wurden.", "It applies to collaborations concluded during your time with the agency.")}
        {monatlich && " "}
        {monatlich && (info.after_bp
          ? t(`Laufende Kooperationen rechnen je Monat nach dieser Regel ab, nach dem Ende mit ${pct(info.after_bp)}.`, `Ongoing collaborations are billed per month under this rule, after the end at ${pct(info.after_bp)}.`)
          : t("Laufende Kooperationen rechnen je Monat nach dieser Regel ab, nach dem Ende ohne Provision der Agentur.", "Ongoing collaborations are billed per month under this rule, after the end without agency commission."))}
      </p>
      {info.deals.length > 0 && (
        <ul>
          {info.deals.map((d, i) => (
            <li key={d.deal_id ?? i}>
              {[d.brand, d.title].filter(Boolean).join(" · ")}
              {d.commission_bp != null ? (d.monthly ? t(` · diesen Monat ${pct(d.commission_bp)}`, ` · this month ${pct(d.commission_bp)}`) : ` · ${pct(d.commission_bp)}`) : ""}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
